import { Field, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import InputText from '../../../components/Inputs/InputText/InputText';
import {
  Notification,
  Button,
  Typography,
  TypographyVariant,
  FileUploader,
} from '@hid-galaxy-ui/galaxy-react';
import styles from './AccountEdit.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';

import { RootStore } from '../../../store/reducers';
import { useHistory } from 'react-router-dom';
import buildPATHS from '../../../utils/paths';
import * as Yup from 'yup';
import { InputsSchema } from '../../../hooks/buildRegisterForm/validationSchema';
import InputSelectCountry from '../../../components/Inputs/InputSelectCountry/InputSelectCountry';
import InputDatePicker from '../../../components/Inputs/InputDatePicker/InputDatePicker';
import cx from 'classnames';
import {
  getAccountDetails,
  updateAccount,
  updateAccountWithEmail,
  uploadFile,
} from '../../../services/app-user/app-user';
import {
  NotificationError,
  NotificationSuccess,
} from '../../../utils/notification';
import OtpValidationForm from '../components/OtpValidationForm/OtpValidationForm';
import { getTranslation } from '../../../utils/functions';
import { IAppProps } from '../../../App';
import {
  IDisabilitiesCategoriesApiResponse,
  IUpdateAccountWithEmailParams,
  IValidateEmailOtpParams,
} from '../../../services/app-user/IAppUser';
import { userStateSelector } from '../../../store/selectors/userSelectors';
import PageTitle from '../../../components/PageTitle';
import PageLoader from '../../../components/PageLoader';
import userActions from '../../../store/actions/userActions';
import { NOTIFICATION_AUTO_CLOSE_DURATION } from '../../../constants/common';
import Modal from '../../../components/Modal';
import { clearLocalStorage } from '../../../utils/sessionUtils';
import InputDropdown from '../../../components/Inputs/InputDropdown/InputDropdown';
import { ISelectOptions } from '../../../utils/interfaces/ISelectOptions';
import DisabilityCategoriesFields from '../components/DisabilityCategories/DisabilityCategories';
import { getTranslatedGenderOptions } from '../accounrHelper';
import { FILE_UPLOAD_MAX_SIZE_MB } from '../../../components/Inputs/InputFileUploadSingle/file-upload-constants';
import { ISetFieldValue } from '../../../utils/interfaces/IFormik';
import allActions from '../../../store/actions';
import { IFileData } from '../create/AccountCreate';
import { getFileNameFromUrl } from '../../../utils/fileUtils';
import { FISCAL_CODE_REGEX } from '../../../constants/regex';
import { accountCreateEditValidationSchema } from '../create/AcoountCreateValidationSchema';
import { getCountryListOptions } from '../../../constants/countryList';
import InputPhoneNumber from '../../../components/Inputs/InputPhoneNumber/InputPhoneNumber';

interface IAccountEditProps extends IAppProps {
  accountData?: any;
}

const AccountEdit = (props: IAccountEditProps) => {
  const { toasts, setToasts } = props;
  const history = useHistory();
  const PATHS = buildPATHS();
  const dispatch = useDispatch();
  const [showEmailChangeConfirmation, setShowEmailChangeConfirmation] =
    useState(false);
  const [tempFormData, setTempFormData] = useState<
    IUpdateAccountWithEmailParams | undefined
  >();
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [uploadAccountDocumentFiles, setUploadAccountDocumentFiles] = useState<
    string[]
  >([]);

  const { accountData } = useSelector((state: RootStore) => ({
    accountData: userStateSelector(state),
  }));

  useEffect(() => {
    if (!accountData?.email) {
      fetchAccountDetails();
    }
  }, []);

  useEffect(() => {
    // Initialize with 2 empty document slots
    setUploadAccountDocumentFiles(accountData?.idDocuments ?? []);
  }, [accountData?.idDocuments]);

  const fetchAccountDetails = async () => {
    try {
      const response = await getAccountDetails();
      dispatch(userActions.setAccountData(response));
    } catch (err) {}
  };

  const accountEditValidationSchema = {
    countryOfBirth: Yup.string().required(
      getTranslation('COUNTRY_OF_BIRTH_REQUIRED'),
    ),
  };

  const buildFormValidation = () => {
    return Yup.object().shape({
      ...accountCreateEditValidationSchema(getTranslation),
      ...accountEditValidationSchema,
    });
  };

  const handleLogout = () => {
    clearLocalStorage();
    dispatch(userActions.resetAccountData());
    history.push(PATHS.login);
  };

  const handleSubmit = async (values: IUpdateAccountWithEmailParams) => {
    if (accountData?.email !== values.email) {
      setShowEmailChangeConfirmation(true);
      setTempFormData(values);
      return;
    }
    updateAccountData(values);
  };

  const handleOtpFormSubmit = (values: IValidateEmailOtpParams) => {
    const dataToUpdate = {
      ...tempFormData,
      otp: values.otp,
      oldEmail: accountData?.email,
    };
    updateAccountData(dataToUpdate);
  };

  const updateAccountData = async (values: IUpdateAccountWithEmailParams) => {
    try {
      if (
        values?.disabilityCategoryCode &&
        values.requiredDocumentsCount !==
          values.documentUrls?.filter((url) => url && url.trim() !== '').length
      ) {
        setToasts([
          ...toasts,
          ...NotificationError('REQUIRED_DOCUMENTS_COUNT_MISMATCH'),
        ]);

        return;
      }
      if (
        values.idDocuments?.filter((url) => url && url.trim() !== '').length !==
        2
      ) {
        setToasts([
          ...toasts,
          ...NotificationError('REQUIRED_DOCUMENTS_ID_COUNT_MISMATCH'),
        ]);
        return;
      }

      const filteredDocumentUrls =
        values.documentUrls?.filter((url) => url && url.trim() !== '') || [];

      // Prepare ID documents array: [front, back] - maintain index positions
      const idDocumentsArray = values.idDocuments || [];
      const hasIdDocuments = idDocumentsArray.some(
        (url) => url && url.trim() !== '',
      );

      const payload: IUpdateAccountWithEmailParams = {
        ...values,
        ...(filteredDocumentUrls.length > 0 && {
          documentUrls: filteredDocumentUrls,
        }),
        ...(hasIdDocuments && {
          idDocuments: idDocumentsArray.filter(
            (url) => url && url.trim() !== '',
          ),
        }),
      };

      const response = await updateAccount(payload);

      if (response) {
        setToasts([
          ...toasts,
          ...NotificationSuccess('ACCOUNT_UPDATED_SUCCESS'),
        ]);
        setTempFormData(undefined);
        if (showOtpForm) {
          setShowOtpForm(false);
          handleLogout();
        } else {
          fetchAccountDetails();
          history.push(PATHS.my_accounts);
        }
      }
    } catch (error) {
      setToasts([
        ...toasts,
        ...NotificationError(
          error.response.data.statusReason || 'AN_ERROR_OCCURED',
        ),
      ]);
    }
  };

  const onConfirmEmailChange = () => {
    setShowEmailChangeConfirmation(false);
    const payload = getPayloadForUpdateAccountWithEmail();

    updateAccountWithEmailAndSendOtp(payload);
  };

  const handleResendOtpFormSubmit = () => {
    const payload = getPayloadForUpdateAccountWithEmail();
    updateAccountWithEmailAndSendOtp(payload);
  };

  const getPayloadForUpdateAccountWithEmail =
    (): IUpdateAccountWithEmailParams => {
      return {
        ...tempFormData,
        newEmail: tempFormData?.email,
        oldEmail: accountData?.email,
      };
    };

  const updateAccountWithEmailAndSendOtp = async (
    values: IUpdateAccountWithEmailParams,
  ) => {
    try {
      const response = await updateAccountWithEmail(values);
      setToasts([...toasts, ...NotificationSuccess('SEND_OTP_SUCCESS')]);
      setShowOtpForm(true);
    } catch (error) {
      setToasts([
        ...toasts,
        ...NotificationError(
          error?.response?.data?.statusReason || 'AN_ERROR_OCCURED',
        ),
      ]);
    }
  };

  const showEmailChangeConfirmationForm = () => {
    return (
      <Modal
        headerText={getTranslation('CONFIRMATION')}
        isFocusTrapActive={false}
        onClose={() => setShowEmailChangeConfirmation(false)}
        footerConfig={{
          primaryBtnLabel: getTranslation('CONFIRM'),
          secondaryBtnLabel: getTranslation('CANCEL'),
          onClickPrimaryBtn: onConfirmEmailChange,
        }}
      >
        <div className="hid-flex hid-flex-column">
          <Typography variant={TypographyVariant.BodyLongMarketing}>
            {`${getTranslation('CONFIRM_EMAIL_CHANGE_MSG_1')} ${getTranslation('CONFIRM_EMAIL_CHANGE_MSG_2')}`}
          </Typography>
        </div>
      </Modal>
    );
  };

  const renderDocumentIdData = (documentIndex: number) => {
    const url = uploadAccountDocumentFiles[documentIndex];
    if (url) {
      return [
        {
          fileDetails: {
            name: getFileNameFromUrl(url),
          },
        },
      ];
    }
    return [];
  };

  const renderDocumentFields = (setFieldValue: ISetFieldValue) => {
    const documentLabels = [
      getTranslation('DOCUMENT_ID_FRONT'),
      getTranslation('DOCUMENT_ID_BACK'),
    ];
    const documentCount = 2;

    return (
      <div className={styles.documentWrapper}>
        {Array.from({ length: documentCount }, (_, index) => (
          <div key={`document-${index}`} className="hid-spacing__mb-04">
            <Typography
              className="hid-spacing__mb-02"
              variant={TypographyVariantEnum.BodyLongMarketing}
            >
              <label className="hid-form-element__label">
                {documentLabels[index] ||
                  `${getTranslation('DOCUMENT')} ${index + 1}`}
                <span className="hid-form-element__label-asterisk">*</span>
              </label>
            </Typography>
            <Field
              key={`idDocuments-${index}`}
              name={`idDocuments-${index}`}
              component={FileUploader}
              buttonConfig={{ label: getTranslation('UPLOAD_DOCUMENT') }}
              data={renderDocumentIdData(index)}
              isMultiple={false}
              onUpload={(e, data) =>
                handleFileUpload(e, data, index, setFieldValue)
              }
              onRemove={(e, data, deletedFile) =>
                handleFileDelete(e, data, deletedFile, index, setFieldValue)
              }
              validateConfig={[
                {
                  maxFileSize: {
                    maxSizeInMb: FILE_UPLOAD_MAX_SIZE_MB,
                    message: getTranslation('FILE_UPLOAD_ERROR_FILE_SIZE'),
                  },
                },
              ]}
            />
          </div>
        ))}
        <Typography variant={TypographyVariantEnum.BodyShortProduct}>
          {getTranslation('DOCUMENT_UPLOAD_INSTRUCTION')}
        </Typography>
      </div>
    );
  };

  const handleFileUpload = async (
    e,
    data,
    documentIndex: number,
    setFieldValue: ISetFieldValue,
  ) => {
    dispatch(allActions.appConfigActions.setLoader(true));

    const filteredNewFiles = data.filter(
      (file) => file.fileDetails instanceof File,
    );
    if (filteredNewFiles.length > 0) {
      const fileUploadResponse = await uploadAccountDocument(filteredNewFiles);
      dispatch(allActions.appConfigActions.setLoader(false));

      if (!fileUploadResponse) {
        return;
      }

      const updatedDocumentUrls = [...uploadAccountDocumentFiles];
      updatedDocumentUrls[documentIndex] = fileUploadResponse[0];
      setDocumentUrls(updatedDocumentUrls, setFieldValue);
    }
  };

  const setDocumentUrls = (
    dataToSet: string[],
    setFieldValue: ISetFieldValue,
  ) => {
    const documentsArray = [...dataToSet, '', ''].slice(0, 2);
    setFieldValue('idDocuments', documentsArray);
    setUploadAccountDocumentFiles(documentsArray);
  };

  const handleFileDelete = (
    e: React.MouseEvent<HTMLElement>,
    data: { fileDetails: { name: string } },
    deletedFile: { name: string },
    documentIndex: number,
    setFieldValue: ISetFieldValue,
  ) => {
    const updatedDocumentUrls = [...uploadAccountDocumentFiles];
    updatedDocumentUrls[documentIndex] = '';
    setDocumentUrls(updatedDocumentUrls, setFieldValue);
  };

  const uploadAccountDocument = async (data: IFileData[]) => {
    return await Promise.all(
      data.map(async (fileObj) => {
        const { fileDetails } = fileObj;
        try {
          const response = await uploadFile(fileDetails);
          return response?.location;
        } catch (error) {
          setToasts([...toasts, ...NotificationError('AN_ERROR_OCCURED')]);
        }
      }),
    );
  };

  const countryOptions = () =>
    getCountryListOptions().map((option) => {
      return {
        label: option.label,
        value: option.key,
      };
    });

  const renderEditForm = () => {
    return (
      <div className={styles.card}>
        <Formik
          enableReinitialize={true}
          initialValues={{
            email: accountData?.email ?? '',
            firstName: accountData?.firstName ?? '',
            lastName: accountData?.lastName ?? '',
            mobileNumber: accountData?.mobileNumber ?? '',
            countryOfBirth: accountData?.countryOfBirth ?? '',
            dateOfBirth: accountData?.dateOfBirth ?? null,
            disabilityCategoryCode: accountData?.disabilityCategoryCode ?? null,
            documentUrls: accountData?.documentUrls ?? undefined,
            requiredDocumentsCount: 0,
            gender: accountData?.gender ?? null,
            placeOfBirth: accountData?.placeOfBirth ?? '',
            idDocuments: accountData?.idDocuments ?? [],
            fiscalCode: accountData?.fiscalCode ?? '',
          }}
          validationSchema={buildFormValidation()}
          onSubmit={handleSubmit}
          // validateOnChange={false}
          // validateOnBlur={false}
        >
          {({ errors, isValid, setFieldValue }) => (
            <>
              <Form className={cx('hid-spacing__px-03', 'register-form')}>
                <Field
                  key="email"
                  component={InputText}
                  label={getTranslation('EMAIL')}
                  type="email"
                  name="email"
                  id="email"
                  required={true}
                />
                <Field
                  key="firstName"
                  component={InputText}
                  label={getTranslation('FIRSTNAME')}
                  name="firstName"
                  id="firstName"
                  required={true}
                />
                <Field
                  key="lastName"
                  component={InputText}
                  label={getTranslation('LASTNAME')}
                  name="lastName"
                  id="lastName"
                  required={true}
                />
                <Field
                  key="gender"
                  component={InputDropdown}
                  label={getTranslation('GENDER')}
                  name="gender"
                  id="gender"
                  options={getTranslatedGenderOptions()}
                  required={true}
                />

                <Field
                  key="mobileNumber"
                  component={InputPhoneNumber}
                  label={getTranslation('MOBILENUMBER')}
                  name="mobileNumber"
                  id="mobileNumber"
                  type="number"
                />

                <Field
                  key="countryOfBirth"
                  component={InputDropdown}
                  label={getTranslation('COUNTRYOFBIRTH')}
                  name="countryOfBirth"
                  id="countryOfBirth"
                  value={accountData?.countryOfBirth}
                  required={true}
                  options={countryOptions()}
                />

                <Field
                  key="dateOfBirth"
                  component={InputDatePicker}
                  label={getTranslation('DATEOFBIRTH')}
                  name="dateOfBirth"
                  id="dateOfBirth"
                  max={new Date()}
                  value={accountData?.dateOfBirth}
                  required={true}
                />

                <Field
                  key="placeOfBirth"
                  component={InputText}
                  label={getTranslation('PLACEOFBIRTH')}
                  name="placeOfBirth"
                  id="placeOfBirth"
                  required={true}
                />
                <Field
                  key="fiscalCode"
                  component={InputText}
                  label={getTranslation('FISCALCODE')}
                  name="fiscalCode"
                  id="fiscalCode"
                  required={true}
                />

                {renderDocumentFields(setFieldValue)}

                <DisabilityCategoriesFields
                  toasts={toasts}
                  setToasts={setToasts}
                  accountData={accountData}
                />

                <div className="hid-flex hid-flex-row hid-flex-jc-end hid-spacing__mt-07">
                  {/* <Button
                    className="hid-spacing__mr-05"
                    label={getTranslation('CANCEL')}
                    onClick={handleCancel}
                    variant="secondary"
                  /> */}

                  <Button label={getTranslation('UPDATE')} type="submit" />
                </div>
              </Form>
            </>
          )}
        </Formik>
        <Notification
          toasts={toasts}
          setToasts={setToasts}
          autoClose={NOTIFICATION_AUTO_CLOSE_DURATION}
        />
      </div>
    );
  };

  const renderOtpForm = () => {
    return (
      <Modal onClose={() => setShowOtpForm(false)} isDisplayCloseIcon={true}>
        <div className="hid-flex hid-flex-column">
          <OtpValidationForm
            registeredEmail={tempFormData?.email}
            toasts={toasts}
            setToasts={setToasts}
            handleOtpFormSubmit={handleOtpFormSubmit}
            showLogo={false}
            handleResendOtpFormSubmit={handleResendOtpFormSubmit}
          />
          ;
        </div>
      </Modal>
    );
  };

  return (
    <>
      {accountData?.email ? renderEditForm() : <PageLoader />}
      {showEmailChangeConfirmation ? showEmailChangeConfirmationForm() : null}
      {showOtpForm ? renderOtpForm() : null}
    </>
  );
};

export default AccountEdit;
